import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Sparkles,
  RefreshCcw,
  Plus,
  Pause,
  Play,
  Info,
  Lightbulb,
  AlertTriangle,
  Target,
  Loader2,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import ShowcaseLogo, { SHOWCASE_LOGO_TICKERS } from "@/components/portfolio/ShowcaseLogo";

/**
 * Landing Page Polish 4 — Portfolio Showcase / Preview Loop.
 * (Correction pass — Feb 2026.)
 *
 * A self-contained, code-native reproduction of the Stockaily
 * Portfolio surface for the public landing page.
 *
 * Safety contract:
 *   • Uses ONLY static illustrative data (see ILLUSTRATIVE below).
 *   • Uses the shared /api/companies/identity cache to hydrate logos
 *     (public, cache-only endpoint, never triggers FMP or the LLM).
 *   • Never calls Portfolio, analysis or watchlist endpoints.
 *   • Never reads authenticated state or user data.
 *   • Never mutates the database.
 */

// ────────────────────────────────────────────────────────────────
// Illustrative portfolio — SINGLE SOURCE OF TRUTH
//
// Rows are the canonical input. Weight / market_value / P/L are
// computed from `shares × current` and `(current − buy) × shares`
// so displayed totals cannot drift from row values.
// Allocation targets:
//   PYPL 20% / MSFT 16% / NEE 16% / JPM 14% / JNJ 13% / CAT 11% / AAPL 10%
// Total market value: $32,450   Cost basis: $25,000   Unrealized P/L: +$7,450
// ────────────────────────────────────────────────────────────────

const RAW_HOLDINGS = [
  // shares × buy ≈ cost_basis; shares × current ≈ market_value target.
  { ticker: "PYPL", name: "PayPal Holdings",   sector: "Financials",       shares: 117.9,   buy: 38.17, current: 55.04, fair_value: 71.42, ai_score: 77, risk: "low",    signal: "opportunity" },
  { ticker: "MSFT", name: "Microsoft Corp.",   sector: "Information Tech.", shares:  9.985,  buy: 420.63, current: 520.00, fair_value: 544.96, ai_score: 74, risk: "low",    signal: "fair" },
  { ticker: "NEE",  name: "NextEra Energy",    sector: "Utilities",         shares: 64.9,    buy: 60.09, current: 80.00, fair_value: 97.60,  ai_score: 80, risk: "low",    signal: "top_opportunity" },
  { ticker: "JPM",  name: "JPMorgan Chase",    sector: "Financials",       shares: 17.5,    buy: 194.29, current: 259.60, fair_value: 298.54, ai_score: 74, risk: "medium", signal: "opportunity" },
  { ticker: "JNJ",  name: "Johnson & Johnson", sector: "Health Care",       shares: 22.8,    buy: 140.35, current: 185.00, fair_value: 207.20, ai_score: 72, risk: "low",    signal: "fair" },
  { ticker: "CAT",  name: "Caterpillar Inc.",  sector: "Industrials",       shares: 10.0,    buy: 230.00, current: 356.95, fair_value: 385.51, ai_score: 70, risk: "medium", signal: "fair" },
  { ticker: "AAPL", name: "Apple Inc.",        sector: "Information Tech.", shares: 10.0,    buy: 350.00, current: 324.50, fair_value: 272.26, ai_score: 47, risk: "medium", signal: "valuation_risk" },
];

function computeDerived() {
  const rows = RAW_HOLDINGS.map((h) => {
    const market_value = h.shares * h.current;
    const cost_basis   = h.shares * h.buy;
    const unrealized_pl = market_value - cost_basis;
    const upside = h.fair_value / h.current - 1;
    return { ...h, market_value, cost_basis, unrealized_pl, upside };
  });
  const total_mv = rows.reduce((s, r) => s + r.market_value, 0);
  const total_cb = rows.reduce((s, r) => s + r.cost_basis, 0);
  const total_pl = rows.reduce((s, r) => s + r.unrealized_pl, 0);
  const enriched = rows.map((r) => ({ ...r, weight: r.market_value / total_mv }));
  return { rows: enriched, total_mv, total_cb, total_pl };
}

const DERIVED = computeDerived();

export const ILLUSTRATIVE = {
  totals: {
    cost_basis: 25000,
    portfolio_value: 32450,
    unrealized_pl: 7450,
    realized_pl: 0,
    total_pl: 7450,
    unrealized_return_pct: 29.8,
    est_fair_value: 36726,
    upside_pct: 13.2,
    weighted_opportunity_score: 72.2,
    risk_level: "medium",
    largest_holding: { ticker: "PYPL", pct: 20 },
    diversification_holdings: 7,
    diversification_sectors: 5,
  },
  performance_1y: {
    portfolio_return_pct: 18.7,
    sp500_return_pct: 12.1,
    relative_pct_points: 6.6,
    final_portfolio_index: 118.7,
    final_sp500_index: 112.1,
    final_portfolio_value: 32450,
    final_est_fair_value: 36726,
    tooltip_date: "31.8.2026",
  },
  holdings: DERIVED.rows,
  insight_biggest_winner: { ticker: "CAT",  pct: 55.2 }, // (356.95 / 230) - 1 ≈ 55.2%
  insight_biggest_loser:  { ticker: "AAPL", pct: -7.3 }, // (324.50 / 350) - 1 ≈ -7.3%
};

// ────────────────────────────────────────────────────────────────
// Weekly index series (52 weeks over one full year) — deterministic,
// three visually distinct paths. See notes below each generator.
// ────────────────────────────────────────────────────────────────

const WEEKS = 52;

// Portfolio path — deterministic but market-driven feel: no repeating
// sine wave, no evenly spaced peaks. Three pullbacks of DIFFERENT
// depth, duration and recovery speed:
//   • Week 8-11  — short, shallow dip (~2 pts) with fast recovery
//   • Week 22-28 — longer, deeper drawdown (~5 pts) followed by choppy base
//   • Week 38-42 — mid-depth pullback (~3.5 pts) with slower recovery
// Plus small sideways phases at 13-16, 30-33 and 47-49.
// End value = 118.70. 52 weekly points spanning one year.
const PORTFOLIO_PIVOTS = [
  100.00, 100.4, 101.7, 102.1, 101.4, 102.9, 103.6, 104.2,               //  0- 7  slow accumulation
  104.7, 103.9, 102.8, 103.1,                                             //  8-11  shallow dip
  104.0, 104.3, 104.2, 104.6,                                             // 12-15  sideways
  105.4, 106.8, 107.5, 108.9, 109.6, 110.3,                               // 16-21  rally
  109.4, 107.8, 106.1, 104.9, 105.6, 104.8, 106.2,                        // 22-28  deeper drawdown + base
  107.3, 108.1, 108.5, 108.9, 108.4,                                      // 29-33  sideways
  109.7, 110.8, 112.1, 113.6, 114.3,                                      // 34-38  strong recovery leg
  113.1, 111.9, 110.8, 111.5,                                             // 39-42  mid pullback
  113.4, 114.6, 115.9, 116.4,                                             // 43-46  recovery
  116.2, 116.5, 117.1,                                                    // 47-49  small sideways
  118.0, 118.4, 118.70,                                                    // 50-51+ final push
];

// Fair Value path — calmer, gradual glide with a small plateau
// mid-year and one modest downward revision reflecting a valuation
// refresh. Ends near 122.7 (≈ $36,726).
const FV_PIVOTS = [
  100.00, 100.5, 101.0, 101.5, 102.1, 102.7, 103.3, 103.9,
  104.4, 105.0, 105.5, 106.0, 106.5, 106.9, 107.3, 107.6,
  108.0, 108.4, 108.9, 109.3, 109.7, 110.0, 110.3, 110.5,
  110.6, 110.4, 110.1, 110.3, 110.7, 111.2, 111.8, 112.4,
  113.1, 113.8, 114.5, 115.2, 115.9, 116.6, 117.3, 118.0,
  118.6, 119.2, 119.8, 120.3, 120.8, 121.2, 121.6, 122.0,
  122.2, 122.4, 122.6, 122.70,
];

// S&P 500 path — independent trajectory with its own irregular
// pullbacks; lower volatility than Portfolio. Ends at 112.10.
const SP500_PIVOTS = [
  100.00, 100.2, 100.9, 101.2, 100.7, 101.4, 101.8, 102.4,
  102.9, 103.1, 102.6, 103.4, 104.1, 104.7, 105.2, 104.9,
  105.7, 106.4, 107.0, 107.3, 106.8, 106.2, 105.9, 106.5,
  107.1, 107.4, 107.0, 106.6, 107.2, 107.9, 108.4, 108.9,
  108.6, 108.3, 108.9, 109.5, 109.8, 109.3, 109.0, 109.6,
  110.2, 110.6, 110.4, 110.9, 111.2, 111.5, 111.3, 111.6,
  111.8, 111.9, 112.0, 112.10,
];

// Validation guards (fail loudly during dev if pivot arrays drift).
if (PORTFOLIO_PIVOTS.length !== WEEKS || FV_PIVOTS.length !== WEEKS || SP500_PIVOTS.length !== WEEKS) {
  // eslint-disable-next-line no-console
  console.warn("[PortfolioShowcase] pivot arrays must have", WEEKS, "entries");
}

const PORTFOLIO_INDEX = PORTFOLIO_PIVOTS;
const SP500_INDEX     = SP500_PIVOTS;
const FV_INDEX        = FV_PIVOTS;

// Build weekly dates covering one full year — ending August 31 2026,
// beginning approximately mid-September 2025 (52 weeks span sampled
// at 40 pivot points ≈ 1 point every ~9 days).
function buildWeeklyDates() {
  const end = new Date(2026, 7, 31); // deterministic
  const totalDays = 365;
  const stride = totalDays / (WEEKS - 1);
  const out = [];
  for (let i = 0; i < WEEKS; i++) {
    const d = new Date(end);
    d.setDate(end.getDate() - Math.round((WEEKS - 1 - i) * stride));
    out.push(d);
  }
  return out;
}

// ────────────────────────────────────────────────────────────────
// Format helpers
// ────────────────────────────────────────────────────────────────
const fmtUSD = (v, digits = 0) =>
  v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: digits, minimumFractionDigits: digits });

const fmtPct = (v, digits = 2) => {
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(digits)}%`;
};

const fmtSigned = (v) => (v >= 0 ? `+${fmtUSD(v)}` : `-${fmtUSD(Math.abs(v))}`);

// ────────────────────────────────────────────────────────────────
// State machine — 40 s total (correction pass: slower, smoother)
// ────────────────────────────────────────────────────────────────

export const STATES = [
  { key: "overview",              durationMs: 4000 },  // 0–4    settle
  { key: "graph_focus",           durationMs: 4000 },  // 4–8    cursor → point, tooltip open (2.5–3s inside)
  { key: "click_analyze",         durationMs: 2000 },  // 8–10   cursor → button, hover + click
  { key: "loading",               durationMs: 3500 },  // 10–13.5 loading banner
  { key: "completed",             durationMs: 2500 },  // 13.5–16 button becomes Update
  { key: "scroll_to_insights",    durationMs: 5000 },  // 16–21  smooth internal scroll
  { key: "holdings_pause",        durationMs: 4000 },  // 21–25  hold + highlight NEE
  { key: "scroll_to_pl",          durationMs: 4000 },  // 25–29  scroll to P/L Breakdown
  { key: "scroll_to_review",      durationMs: 4000 },  // 29–33  scroll to AI Review
  { key: "review_pause",          durationMs: 5000 },  // 33–38  hold review
  { key: "reset",                 durationMs: 2000 },  // 38–40  soft fade back
];

const TOTAL_MS = STATES.reduce((s, x) => s + x.durationMs, 0);

// State helpers
const stateKey = (i) => STATES[i]?.key;

// ────────────────────────────────────────────────────────────────
// Sub-component: growth chart (SVG, three series, controlled tooltip)
// ────────────────────────────────────────────────────────────────

function GrowthChart({ showTooltip, reducedMotion, t, chartRef, graphPointRef }) {
  const dates = useMemo(() => buildWeeklyDates(), []);
  const W = 900;
  const H = 260;
  const PADL = 46;
  const PADR = 58;
  const PADT = 18;
  const PADB = 34;
  const innerW = W - PADL - PADR;
  const innerH = H - PADT - PADB;

  const minIdx = 98;
  const maxIdx = 124;
  const idxRange = maxIdx - minIdx;

  const xForIdx = (i) => PADL + (i / (dates.length - 1)) * innerW;
  const yForIdx = (val) => PADT + (1 - (val - minIdx) / idxRange) * innerH;

  // Restrained line interpolation — straight polyline segments so the
  // chart does not create artificially smooth curves between points.
  // (The dense pivot sets still give the eye a natural flowing shape.)
  const linePath = (values) => {
    const pts = values.map((v, i) => [xForIdx(i), yForIdx(v)]);
    let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      d += ` L${pts[i][0].toFixed(1)},${pts[i][1].toFixed(1)}`;
    }
    return d;
  };

  const portfolioPath = linePath(PORTFOLIO_INDEX);
  const sp500Path     = linePath(SP500_INDEX);
  const fairPath      = linePath(FV_INDEX);

  // Tooltip anchor — pick a point near the recent peak (portfolio).
  const tipI = Math.max(0, PORTFOLIO_INDEX.length - 4);
  const tipX = xForIdx(tipI);
  const tipY = yForIdx(PORTFOLIO_INDEX[tipI]);
  const tipTopPct = (tipY / H) * 100;

  const gridVals = [100, 105, 110, 115, 120];
  const rightAxis = [
    { pct: 100 / (maxIdx - minIdx) * (120 - minIdx), label: "$37k" },
    { pct: 100 / (maxIdx - minIdx) * (110 - minIdx), label: "$34k" },
    { pct: 100 / (maxIdx - minIdx) * (100 - minIdx), label: "$31k" },
  ];

  // Position the tooltip inside the graph area — clamp above the point.
  const tipLeftPct = Math.max(6, Math.min(72, (tipX / W) * 100 - 10));

  return (
    <div className="relative rounded-2xl border border-white/10 bg-card/40 p-4 sm:p-5" data-testid="showcase-growth-chart" ref={chartRef}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-[15px] sm:text-[17px] font-semibold text-foreground">{t("landing.showcase_growth_title")}</div>
          <div className="text-[11px] sm:text-xs text-muted-foreground">{t("landing.showcase_growth_sub")}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.08)] px-2 py-0.5 text-[10.5px] font-medium text-[#3DEBFF]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3DEBFF]" />
              {t("landing.showcase_time_weighted")}
            </span>
            <span className="inline-flex items-center rounded-full border border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.08)] px-2 py-0.5 text-[10.5px] font-semibold text-[#2BFF8A]">
              {t("landing.showcase_good")}
            </span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1 rounded-full border border-white/10 bg-black/20 p-1 text-[11px]" data-testid="showcase-range-controls">
          {["1M", "3M", "6M", "YTD", "1Y", "ALL"].map((r) => (
            <span
              key={r}
              data-testid={`showcase-range-${r}`}
              className={`px-2 py-0.5 rounded-full ${r === "1Y" ? "bg-[rgba(61,235,255,0.14)] text-[#3DEBFF] border border-[#3DEBFF]/40" : "text-muted-foreground"}`}
              aria-current={r === "1Y" ? "true" : undefined}
            >
              {r}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 my-3">
        <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
          <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">{t("landing.showcase_portfolio_return")}</div>
          <div className="text-[15px] font-semibold text-[#2BFF8A] tabular">+18.70%</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
          <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">{t("landing.showcase_sp500_return")}</div>
          <div className="text-[15px] font-semibold text-[#A78BFA] tabular">+12.10%</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
          <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">{t("landing.showcase_relative_perf")}</div>
          <div className="text-[15px] font-semibold text-[#2BFF8A] tabular">+6.60%</div>
        </div>
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" role="img" aria-label={t("landing.showcase_growth_title")} data-testid="showcase-growth-svg">
          <defs>
            <linearGradient id="portGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#3DEBFF" stopOpacity="0.30" />
              <stop offset="100%" stopColor="#3DEBFF" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="fvGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#2BFF8A" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#2BFF8A" stopOpacity="0" />
            </linearGradient>
          </defs>
          {gridVals.map((v) => (
            <g key={v}>
              <line x1={PADL} x2={W - PADR} y1={yForIdx(v)} y2={yForIdx(v)} stroke="#ffffff" strokeOpacity="0.06" />
              <text x={PADL - 8} y={yForIdx(v) + 4} textAnchor="end" fontSize="10" fill="rgba(255,255,255,0.5)" fontFamily="ui-monospace, SFMono-Regular, monospace">
                {v}
              </text>
            </g>
          ))}
          <text x={-H / 2} y={12} transform={`rotate(-90)`} fontSize="10" fill="rgba(255,255,255,0.35)" textAnchor="middle">{t("landing.showcase_axis_perf")}</text>
          {rightAxis.map((r, i) => (
            <text key={i} x={W - PADR + 8} y={PADT + (1 - r.pct / 100) * innerH + 4} fontSize="10" fill={i === rightAxis.length - 1 ? "rgba(43,255,138,0.55)" : "rgba(43,255,138,0.75)"} fontFamily="ui-monospace, SFMono-Regular, monospace">{r.label}</text>
          ))}
          {[0, Math.floor(WEEKS * 0.2), Math.floor(WEEKS * 0.4), Math.floor(WEEKS * 0.6), Math.floor(WEEKS * 0.8), WEEKS - 1].map((i) => {
            const d = dates[i];
            const label = d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
            return (
              <text key={i} x={xForIdx(i)} y={H - 10} fontSize="10" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontFamily="ui-monospace, SFMono-Regular, monospace">
                {label}
              </text>
            );
          })}
          {/* Fair Value line + area (green) */}
          <path d={`${fairPath} L ${xForIdx(WEEKS - 1)},${H - PADB} L ${PADL},${H - PADB} Z`} fill="url(#fvGrad)" opacity="0.55" />
          <path d={fairPath} fill="none" stroke="#2BFF8A" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" data-testid="showcase-series-fv" />
          {/* Portfolio line + area (cyan) */}
          <path d={`${portfolioPath} L ${xForIdx(WEEKS - 1)},${H - PADB} L ${PADL},${H - PADB} Z`} fill="url(#portGrad)" />
          <path d={portfolioPath} fill="none" stroke="#3DEBFF" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 6px rgba(61,235,255,0.35))" }} data-testid="showcase-series-portfolio" />
          {/* S&P 500 dashed purple */}
          <path d={sp500Path} fill="none" stroke="#A78BFA" strokeWidth="2" strokeDasharray="5 5" strokeLinejoin="round" strokeLinecap="round" data-testid="showcase-series-sp500" />
          {/* Selected marker + vertical guide (visible only while tooltip is open) */}
          {showTooltip && !reducedMotion ? (
            <g data-testid="showcase-graph-selection">
              <line x1={tipX} x2={tipX} y1={PADT + 4} y2={H - PADB} stroke="#3DEBFF" strokeOpacity="0.35" strokeDasharray="3 3" />
              <circle cx={tipX} cy={tipY} r="5" fill="#3DEBFF" stroke="#0b0d10" strokeWidth="1.5" ref={graphPointRef} data-testid="showcase-graph-point" />
              <circle cx={tipX} cy={tipY} r="9" fill="none" stroke="#3DEBFF" strokeOpacity="0.4" />
            </g>
          ) : (
            <circle cx={tipX} cy={tipY} r="3" fill="#3DEBFF" stroke="#0b0d10" strokeWidth="1.5" opacity="0.55" ref={graphPointRef} data-testid="showcase-graph-point" />
          )}
        </svg>

        {/* Comparison tooltip — controlled by parent via `showTooltip`. */}
        <AnimatePresence>
          {showTooltip && !reducedMotion ? (
            <motion.div
              key="graph-tooltip"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="absolute pointer-events-none rounded-lg border border-white/15 bg-[rgba(12,14,18,0.94)] backdrop-blur px-3 py-2 text-[11px] shadow-[0_10px_28px_rgba(0,0,0,0.55)] z-10"
              style={{ left: `${tipLeftPct}%`, top: `calc(${tipTopPct}% - 12px)`, transform: "translateY(-100%)", maxWidth: 260 }}
              data-testid="showcase-graph-tooltip"
            >
              <div className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground mb-1">
                {t("landing.showcase_tooltip_date")} {ILLUSTRATIVE.performance_1y.tooltip_date}
              </div>
              <TipRow color="#3DEBFF" label={t("landing.showcase_tooltip_portfolio_index")} value="118.70" />
              <TipRow color="#3DEBFF" label={t("landing.showcase_tooltip_portfolio_value")} value={fmtUSD(32450)} />
              <TipRow color="#A78BFA" label={t("landing.showcase_tooltip_sp500_index")} value="112.10" />
              <TipRow color="#2BFF8A" label={t("landing.showcase_tooltip_est_fv")} value={fmtUSD(36726)} />
              <div className="mt-1 pt-1 border-t border-white/10 space-y-0.5">
                <TipRow label={t("landing.showcase_tooltip_portfolio_since")} value="+18.70%" valueColor="#2BFF8A" muted />
                <TipRow label={t("landing.showcase_tooltip_sp500_since")}     value="+12.10%" valueColor="#A78BFA" muted />
                <TipRow label={t("landing.showcase_tooltip_relative")}        value="+6.60%"  valueColor="#2BFF8A" muted />
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px]" data-testid="showcase-legend">
        <LegendItem color="#2BFF8A" label={t("landing.showcase_legend_fv")} />
        <LegendItem color="#3DEBFF" label={t("landing.showcase_legend_portfolio")} />
        <LegendItem color="#A78BFA" label={t("landing.showcase_legend_sp500")} dashed />
      </div>
    </div>
  );
}

function TipRow({ color, label, value, valueColor, muted }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex items-center gap-1.5">
        {color ? <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} /> : null}
        <span className={muted ? "text-muted-foreground" : "text-foreground/85"}>{label}</span>
      </span>
      <span className="tabular font-medium" style={{ color: valueColor || "rgba(255,255,255,0.92)" }}>{value}</span>
    </div>
  );
}
function LegendItem({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span className={`inline-block h-0.5 w-4`} style={{ background: dashed ? "transparent" : color, borderTop: dashed ? `1.5px dashed ${color}` : "none" }} />
      {label}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
// KPI cards row
// ────────────────────────────────────────────────────────────────

function KpiCards({ t }) {
  const rows = [
    { key: "value",   testId: "showcase-kpi-value",   title: t("landing.showcase_kpi_value"),  value: fmtUSD(32450), sub: t("landing.showcase_kpi_value_hint"), delta: "+29.8%", deltaColor: "#2BFF8A", accent: "cyan"  },
    { key: "fv",      testId: "showcase-kpi-fv",      title: t("landing.showcase_kpi_fv"),     value: fmtUSD(36726), sub: null,                                 delta: "+13.2%", deltaColor: "#2BFF8A", accent: "green" },
    { key: "upside",  testId: "showcase-kpi-upside",  title: t("landing.showcase_kpi_upside"), value: "+13.2%",       sub: t("landing.showcase_kpi_upside_hint"), delta: null,     deltaColor: "#2BFF8A", accent: "cyan"  },
    { key: "pnl",     testId: "showcase-kpi-pnl",     title: t("landing.showcase_kpi_pnl"),    value: "+" + fmtUSD(7450), sub: null,                             delta: "+29.8%", deltaColor: "#2BFF8A", accent: "green" },
    { key: "score",   testId: "showcase-kpi-score",   title: t("landing.showcase_kpi_score"),  value: "72.2",         sub: t("landing.showcase_kpi_score_hint"), delta: null,     deltaColor: null,      accent: "green" },
    { key: "risk",    testId: "showcase-kpi-risk",    title: t("landing.showcase_kpi_risk"),   value: <span className="inline-flex items-center rounded-md border border-[#FFB72B]/40 bg-[rgba(255,183,43,0.1)] px-2 py-0.5 text-[13px] text-[#FFB72B]">{t("landing.showcase_risk_medium")}</span>, sub: t("landing.showcase_kpi_score_hint"), delta: null, deltaColor: null, accent: "indigo" },
  ];
  const dotColors = {
    cyan:   "bg-[#3DEBFF] shadow-[0_0_14px_rgba(61,235,255,0.45)]",
    green:  "bg-[#2BFF8A] shadow-[0_0_14px_rgba(43,255,138,0.4)]",
    indigo: "bg-[#A78BFA] shadow-[0_0_14px_rgba(167,139,250,0.4)]",
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3" data-testid="showcase-kpi-row">
      {rows.map((r) => (
        <div key={r.key} data-testid={r.testId} className="relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-card/60 backdrop-blur px-3 sm:px-3.5 py-3">
          <div className="flex items-start justify-between gap-2 min-h-[26px]">
            {/* Sub-pass 2A (2026-02-17) — remove line-clamp-2 so
                full KPI titles like "Weighted Opportunity Score" or
                "Estimated Fair Value" are never truncated at
                normal desktop widths.  `break-words hyphens-auto`
                lets long titles wrap gracefully within the column. */}
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground leading-[1.3] break-words hyphens-auto pr-1" data-testid={`${r.testId}-title`}>
              {r.title}
            </div>
            <span className={`h-2 w-2 shrink-0 rounded-full mt-0.5 ${dotColors[r.accent]}`} />
          </div>
          <div className="mt-1.5 text-[16px] sm:text-[19px] font-semibold tabular text-foreground">{r.value}</div>
          {r.sub ? <div className="mt-1 text-[10.5px] text-muted-foreground leading-[1.35] break-words hyphens-auto line-clamp-2 min-h-[28px]" data-testid={`${r.testId}-sub`}>{r.sub}</div> : <div className="mt-1 min-h-[28px]" aria-hidden="true" />}
          {r.delta ? (
            <div className="mt-1.5 inline-flex self-start items-center rounded-full border border-white/10 bg-black/20 px-1.5 py-0.5 text-[10.5px] tabular" style={{ color: r.deltaColor }}>
              {r.delta}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Insight cards (arithmetic-coherent — winner/loser fixed)
// ────────────────────────────────────────────────────────────────

function InsightCards({ highlightNEE, t }) {
  const cards = [
    { key: "top",   label: t("landing.showcase_top_opportunity"),  desc: t("landing.showcase_top_opportunity_desc"),  ticker: "NEE",  value: "+22.0%",  valueColor: "#2BFF8A", border: "#2BFF8A", testId: "showcase-insight-top" },
    { key: "risk",  label: t("landing.showcase_largest_risk"),     desc: t("landing.showcase_largest_risk_desc"),     ticker: "AAPL", value: "-16.1%",  valueColor: "#FF3DA6", border: "#FF3DA6", testId: "showcase-insight-risk" },
    { key: "wt",    label: t("landing.showcase_highest_weight"),   desc: t("landing.showcase_highest_weight_desc"),   ticker: "PYPL", value: "20.0%",   valueColor: "#A78BFA", border: "#A78BFA", testId: "showcase-insight-weight" },
    { key: "win",   label: t("landing.showcase_biggest_winner"),   desc: t("landing.showcase_biggest_winner_desc"),   ticker: "CAT",  value: "+55.2%",  valueColor: "#2BFF8A", border: "#2BFF8A", testId: "showcase-insight-winner" },
    { key: "lose",  label: t("landing.showcase_biggest_loser"),    desc: t("landing.showcase_biggest_loser_desc"),    ticker: "AAPL", value: "-7.3%",   valueColor: "#FF3DA6", border: "#FF3DA6", testId: "showcase-insight-loser" },
    { key: "conc",  label: t("landing.showcase_concentration"),    desc: t("landing.showcase_concentration_desc"),    ticker: "FIN",  value: "34.0%",   valueColor: "#A78BFA", border: "#A78BFA", testId: "showcase-insight-concentration" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3" data-testid="showcase-insight-row">
      {cards.map((c) => (
        <motion.div
          key={c.key}
          animate={highlightNEE && c.key === "top" ? { boxShadow: "0 0 0 1.5px rgba(43,255,138,0.55), 0 0 32px rgba(43,255,138,0.28)" } : { boxShadow: "0 0 0 1px rgba(255,255,255,0.06)" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-xl border border-white/10 bg-card/50 px-3 py-3"
          data-testid={c.testId}
        >
          <div className="absolute top-0 left-3 right-3 h-[2px] rounded-full" style={{ background: c.border, opacity: 0.75 }} />
          <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">{c.label}</div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="font-semibold text-[13px] text-foreground">{c.ticker}</span>
            <span className="tabular text-[13px]" style={{ color: c.valueColor }}>{c.value}</span>
          </div>
          <p className="mt-1 text-[10.5px] leading-snug text-muted-foreground line-clamp-2">{c.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Holdings table (with real CompanyLogo hydration)
// ────────────────────────────────────────────────────────────────

function HoldingsTable({ highlightTicker, t }) {
  const riskLabel = { low: t("landing.showcase_risk_low"), medium: t("landing.showcase_risk_med"), high: t("landing.showcase_risk_high") };
  const riskStyle = {
    low:    "border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.08)] text-[#2BFF8A]",
    medium: "border-[#FFB72B]/40 bg-[rgba(255,183,43,0.08)] text-[#FFB72B]",
    high:   "border-[#FF3DA6]/40 bg-[rgba(255,61,166,0.08)] text-[#FF3DA6]",
  };
  const signalLabel = {
    top_opportunity: t("landing.showcase_signal_top_opportunity"),
    opportunity:     t("landing.showcase_signal_opportunity"),
    fair:            t("landing.showcase_signal_fair"),
    valuation_risk:  t("landing.showcase_signal_valuation_risk"),
  };
  const signalColor = {
    top_opportunity: "#2BFF8A",
    opportunity:     "#3DEBFF",
    fair:            "rgba(255,255,255,0.65)",
    valuation_risk:  "#FF3DA6",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-card/40 overflow-hidden" data-testid="showcase-holdings-table">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] min-w-[960px]">
          <thead className="bg-black/20 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">{t("landing.showcase_col_ticker")}</th>
              <th className="text-left px-3 py-2 font-medium">{t("landing.showcase_col_company")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_shares")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_buy")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_current")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_mv")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_pnl")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_weight")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_fv")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_upside")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_score")}</th>
              <th className="text-right px-3 py-2 font-medium">{t("landing.showcase_col_risk")}</th>
            </tr>
          </thead>
          <tbody>
            {ILLUSTRATIVE.holdings.map((h) => {
              const pnlColor = h.unrealized_pl >= 0 ? "#2BFF8A" : "#FF3DA6";
              const upsideColor = h.upside >= 0 ? "#2BFF8A" : "#FF3DA6";
              const rowHighlight = highlightTicker === h.ticker;
              return (
                <tr
                  key={h.ticker}
                  data-testid={`showcase-row-${h.ticker}`}
                  className={`border-t border-white/5 transition-colors ${rowHighlight ? "bg-[rgba(43,255,138,0.06)]" : "hover:bg-white/[0.02]"}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <ShowcaseLogo
                        ticker={h.ticker}
                        name={h.name}
                        size="xs"
                        testId={`showcase-logo-${h.ticker}`}
                      />
                      <span className="font-semibold text-foreground">{h.ticker}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-foreground/85">
                    <div>{h.name}</div>
                    <div className="text-[10.5px]" style={{ color: signalColor[h.signal] }}>{signalLabel[h.signal]}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular text-foreground/85">{h.shares.toFixed(h.shares % 1 === 0 ? 0 : 2)}</td>
                  <td className="px-3 py-2 text-right tabular text-foreground/85">{fmtUSD(h.buy, 2)}</td>
                  <td className="px-3 py-2 text-right tabular text-foreground/85">{fmtUSD(h.current, 2)}</td>
                  <td className="px-3 py-2 text-right tabular text-foreground">{fmtUSD(h.market_value, 0)}</td>
                  <td className="px-3 py-2 text-right tabular" style={{ color: pnlColor }}>{fmtSigned(h.unrealized_pl)}</td>
                  <td className="px-3 py-2 text-right tabular text-foreground/85">{(h.weight * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular text-foreground/85">{fmtUSD(h.fair_value, 2)}</td>
                  <td className="px-3 py-2 text-right tabular" style={{ color: upsideColor }}>{fmtPct(h.upside * 100, 1)}</td>
                  <td className="px-3 py-2 text-right tabular text-foreground">{h.ai_score}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10.5px] ${riskStyle[h.risk]}`}>{riskLabel[h.risk]}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Profit / Loss Breakdown section
// ────────────────────────────────────────────────────────────────

function ProfitLossBreakdown({ t }) {
  const cards = [
    { key: "unrealized", testId: "showcase-pl-unrealized", label: t("landing.showcase_pl_unrealized"), value: "+" + fmtUSD(7450, 0), color: "#2BFF8A", icon: TrendingUp },
    { key: "realized",   testId: "showcase-pl-realized",   label: t("landing.showcase_pl_realized"),   value: fmtUSD(0, 0),          color: "rgba(255,255,255,0.85)", icon: RefreshCcw },
    { key: "total",      testId: "showcase-pl-total",      label: t("landing.showcase_pl_total"),      value: "+" + fmtUSD(7450, 0), color: "#2BFF8A", icon: TrendingUp },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-card/40 p-4 sm:p-5" data-testid="showcase-pl-breakdown">
      <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-3">{t("landing.showcase_pl_breakdown_title")}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              data-testid={c.testId}
              data-pl-tone="neutral"
              className="relative rounded-xl border border-white/10 bg-black/20 px-4 py-3"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">{c.label}</div>
                <Icon className="w-3.5 h-3.5 text-muted-foreground/70" />
              </div>
              <div className="text-[20px] font-semibold tabular" style={{ color: c.color }}>{c.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// AI Portfolio Review
// ────────────────────────────────────────────────────────────────

function AIPortfolioReview({ t }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-card/60 backdrop-blur"
      style={{ boxShadow: "0 0 32px rgba(61,235,255,0.10)" }}
      data-testid="showcase-ai-review"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] rounded-full bg-[#3DEBFF] shadow-[0_0_14px_rgba(61,235,255,0.55)]" />
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#3DEBFF]/50 bg-[rgba(61,235,255,0.12)]">
              <Sparkles className="h-4 w-4 text-[#3DEBFF]" />
            </span>
            <div className="text-[15px] sm:text-[17px] font-semibold">{t("landing.showcase_review_title")}</div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[10.5px]">
            <span className="rounded-full border border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.08)] px-2 py-0.5 text-[#3DEBFF] font-medium">{t("landing.showcase_review_badge")}</span>
            <span className="rounded-full border border-white/15 bg-black/20 px-2 py-0.5 text-muted-foreground">{t("landing.showcase_review_badge_example")}</span>
            <span className="rounded-full border border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.08)] px-2 py-0.5 text-[#2BFF8A]">{t("landing.showcase_review_up_to_date")}</span>
          </div>
        </div>

        <p className="text-[13px] leading-[1.6] text-foreground/85">{t("landing.showcase_review_summary")}</p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2BFF8A]">
              <Lightbulb className="h-3.5 w-3.5" /> {t("landing.showcase_review_strengths")}
            </div>
            <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground/85 list-disc pl-5" data-testid="showcase-review-strengths">
              <li>{t("landing.showcase_review_strengths_1")}</li>
              <li>{t("landing.showcase_review_strengths_2")}</li>
              <li>{t("landing.showcase_review_strengths_3")}</li>
            </ul>
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#FF3DA6]">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("landing.showcase_review_weaknesses")}
            </div>
            <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground/85 list-disc pl-5" data-testid="showcase-review-weaknesses">
              <li>{t("landing.showcase_review_weaknesses_1")}</li>
              <li>{t("landing.showcase_review_weaknesses_2")}</li>
            </ul>
          </div>
        </div>

        <div className="mt-4">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#A78BFA]">
            <Target className="h-3.5 w-3.5" /> {t("landing.showcase_review_concentration")}
          </div>
          <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground/85 list-disc pl-5">
            <li>{t("landing.showcase_review_concentration_1")}</li>
          </ul>
        </div>

        <div className="mt-4">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#3DEBFF]">
            <Info className="h-3.5 w-3.5" /> {t("landing.showcase_review_priorities")}
          </div>
          <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground/85 list-disc pl-5" data-testid="showcase-review-priorities">
            <li>{t("landing.showcase_review_priorities_1")}</li>
            <li>{t("landing.showcase_review_priorities_2")}</li>
            <li>{t("landing.showcase_review_priorities_3")}</li>
          </ul>
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground/85 italic">
          {t("landing.showcase_review_confidence")}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Loading banner (State: loading)
// ────────────────────────────────────────────────────────────────

function LoadingBanner({ t }) {
  return (
    <div
      data-testid="showcase-loading-banner"
      className="relative overflow-hidden rounded-xl border border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.06)] px-4 sm:px-5 py-3.5 shadow-[0_0_24px_rgba(61,235,255,0.18)]"
    >
      <div className="absolute top-0 left-4 right-4 h-[2px] rounded-full bg-[#3DEBFF] shadow-[0_0_12px_rgba(61,235,255,0.6)] animate-pulse" />
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(61,235,255,0.12)] border border-[#3DEBFF]/40">
          <Sparkles className="h-4 w-4 text-[#3DEBFF] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-[#3DEBFF] font-medium">
              {t("landing.showcase_premium_ai")}
            </span>
            <Loader2 className="h-3 w-3 text-[#3DEBFF]/80 animate-spin" />
          </div>
          <div className="mt-0.5 text-[15px] sm:text-[16px] leading-snug font-medium text-foreground">
            {t("landing.showcase_loading_msg")}
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Simulated cursor — smoothly animated between named targets
// ────────────────────────────────────────────────────────────────

function FakeCursor({ cx, cy, clicking, hover, visible }) {
  return (
    <motion.div
      aria-hidden="true"
      className="absolute pointer-events-none z-30"
      // Translate so the cursor tip's visual center sits on (cx, cy).
      // The pointer glyph is 18×22 and its "tip" is near (5, 4), so we
      // shift by (-5, -4) to center on the calculated point.
      style={{ left: 0, top: 0 }}
      initial={false}
      animate={{
        x: cx - 5,
        y: cy - 4,
        opacity: visible ? 1 : 0,
        scale: clicking ? 0.88 : 1,
      }}
      transition={{
        x: { duration: 1.1, ease: [0.4, 0, 0.2, 1] },
        y: { duration: 1.1, ease: [0.4, 0, 0.2, 1] },
        scale: { duration: 0.18 },
        opacity: { duration: 0.4 },
      }}
      data-testid="showcase-fake-cursor"
      data-cursor-cx={cx}
      data-cursor-cy={cy}
    >
      <svg width="18" height="22" viewBox="0 0 18 22" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.6))" }}>
        <path d="M1 1 L1 17 L5 13 L8 20 L11 19 L8 12 L14 11 Z" fill="white" stroke="black" strokeWidth="1" />
      </svg>
      {hover && !clicking ? (
        <span className="absolute -top-2 -left-2 w-8 h-8 rounded-full border border-[#3DEBFF]/50" />
      ) : null}
      {clicking ? (
        <>
          <span className="absolute -top-2 -left-2 w-8 h-8 rounded-full border-2 border-[#3DEBFF] animate-ping" />
          <span className="absolute -top-2 -left-2 w-8 h-8 rounded-full border border-[#3DEBFF]/70" />
        </>
      ) : null}
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────
// Main Showcase — state machine loop
// ────────────────────────────────────────────────────────────────

export default function PortfolioShowcase() {
  const { t } = useTranslation();
  const prefersReduced = useReducedMotion();
  const reducedMotion = !!prefersReduced;
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const chartRef = useRef(null);
  const insightsRef = useRef(null);
  const plRef = useRef(null);
  const reviewRef = useRef(null);
  const analyzeBtnRef = useRef(null);
  const graphPointRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(reducedMotion);
  const [stateIndex, setStateIndex] = useState(0);
  const [manuallyInteracted, setManuallyInteracted] = useState(false);
  const [cursorTarget, setCursorTarget] = useState(null); // { x, y, hover, clicking, visible }
  const [analyzeHover, setAnalyzeHover] = useState(false);
  const [analyzePressed, setAnalyzePressed] = useState(false);

  // Tickers list — deterministic showcase set.
  const tickers = useMemo(() => ILLUSTRATIVE.holdings.map((h) => h.ticker), []);

  // Advance state on interval when running.
  useEffect(() => {
    if (paused || reducedMotion || !visible) return;
    const dur = STATES[stateIndex].durationMs;
    const id = setTimeout(() => {
      setStateIndex((i) => (i + 1) % STATES.length);
    }, dur);
    return () => clearTimeout(id);
  }, [paused, reducedMotion, visible, stateIndex]);

  // Smooth internal scroll driven by requestAnimationFrame.
  const rafRef = useRef(null);
  const animateScrollTo = useCallback((target, durationMs) => {
    const el = scrollRef.current;
    if (!el) return;
    if (reducedMotion) { el.scrollTop = target; return; }
    const start = el.scrollTop;
    const delta = target - start;
    const t0 = performance.now();
    const ease = (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2); // easeInOutQuad
    const step = (now) => {
      const p = Math.min(1, (now - t0) / durationMs);
      el.scrollTop = start + delta * ease(p);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  }, [reducedMotion]);

  // Scroll behavior — targets driven by state key.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const key = stateKey(stateIndex);
    const anchorTop = (ref) => {
      if (!ref.current || !el) return 0;
      const parentTop = el.getBoundingClientRect().top;
      const nodeTop = ref.current.getBoundingClientRect().top;
      return el.scrollTop + (nodeTop - parentTop) - 16;
    };
    let target = 0;
    let dur = 900;
    if (key === "overview" || key === "reset")     { target = 0; dur = 700; }
    else if (key === "graph_focus")                { target = anchorTop(chartRef); dur = 700; }
    else if (key === "click_analyze")              { target = 0; dur = 500; }
    else if (key === "loading" || key === "completed") { target = 0; dur = 400; }
    else if (key === "scroll_to_insights")         { target = anchorTop(insightsRef); dur = 4500; }
    else if (key === "holdings_pause")             { target = anchorTop(insightsRef); dur = 400; }
    else if (key === "scroll_to_pl")               { target = anchorTop(plRef);       dur = 3800; }
    else if (key === "scroll_to_review")           { target = anchorTop(reviewRef);   dur = 3800; }
    else if (key === "review_pause")               { target = anchorTop(reviewRef);   dur = 400; }
    animateScrollTo(target, dur);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateIndex, animateScrollTo]);

  // IntersectionObserver — pause when off-screen.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setVisible(e.isIntersecting && e.intersectionRatio > 0.1);
      },
      { threshold: [0, 0.1, 0.5] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const currentState = stateKey(stateIndex);
  const showLoading = currentState === "loading";
  const showCompleted = ["completed", "scroll_to_insights", "holdings_pause", "scroll_to_pl", "scroll_to_review", "review_pause"].includes(currentState);
  const buttonLabel = showLoading
    ? t("landing.showcase_analyze_running")
    : showCompleted
    ? t("landing.showcase_update_analysis")
    : t("landing.showcase_analyze_portfolio");

  // Tooltip: only during `graph_focus`. Never at end.
  const showTooltip = currentState === "graph_focus" && !reducedMotion;
  // NEE highlight during holdings pause.
  const highlightNEE = currentState === "holdings_pause";

  // Cursor route — deliberate story: graph point → Analyze Portfolio → click.
  // Anchored to real DOM refs (not hard-coded pixel offsets), so the
  // button target stays correct across English/German widths and any
  // future layout changes.
  useEffect(() => {
    if (reducedMotion) { setCursorTarget(null); setAnalyzeHover(false); setAnalyzePressed(false); return; }
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const rectOf = (el) => {
      if (!el || !scrollEl) return null;
      const parent = scrollEl.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      // Return pixel offsets in the SCROLL CONTENT coordinate system
      // (0,0 = top-left of the inner content). An absolutely-positioned
      // element inside a scroll container is placed against these
      // coordinates, and it scrolls naturally with the content.
      const cx = (r.left + r.width / 2) - parent.left;
      const cy = (r.top  + r.height / 2) - parent.top + scrollEl.scrollTop;
      return { cx, cy };
    };

    const key = stateKey(stateIndex);
    let target = null;
    let hover = false;
    let clicking = false;
    let btnPressed = false;
    let btnHover = false;

    if (key === "overview") {
      const g = rectOf(graphPointRef.current) || rectOf(chartRef.current);
      if (g) target = { cx: g.cx, cy: g.cy, visible: true, hover: false, clicking: false };
    } else if (key === "graph_focus") {
      const g = rectOf(graphPointRef.current);
      if (g) target = { cx: g.cx, cy: g.cy, visible: true, hover: false, clicking: true };
    } else if (key === "click_analyze") {
      // Two-phase interaction inside this state:
      //   phase A (0 – 1300 ms): cursor eases into the exact CENTER of
      //     the Analyze Portfolio button and shows only a hover ring
      //   phase B (1300 ms – end): cursor "presses" — click ring + subtle
      //     button press state — before loading begins.
      const b = rectOf(analyzeBtnRef.current);
      if (b) {
        target = { cx: b.cx, cy: b.cy, visible: true, hover: true, clicking: false };
        btnHover = true;
        btnPressed = false;
      }
    } else if (key === "loading" || key === "completed") {
      target = null;
    } else {
      target = null;
    }

    setCursorTarget(target);
    setAnalyzeHover(btnHover);
    setAnalyzePressed(btnPressed);

    // Phase B of click_analyze — after the cursor has centered itself
    // on the button (≈1100 ms animation + 200 ms breathing room), fire
    // the click ring and subtle button press state.
    let pressTimer = null;
    if (key === "click_analyze") {
      pressTimer = setTimeout(() => {
        setAnalyzePressed(true);
        setCursorTarget((prev) => (prev ? { ...prev, clicking: true } : prev));
      }, 1300);
    }
    return () => { if (pressTimer) clearTimeout(pressTimer); };
  }, [stateIndex, reducedMotion]);

  const onManualPause = useCallback(() => setPaused((p) => !p), []);
  const onManualInteract = useCallback(() => {
    if (!manuallyInteracted) setManuallyInteracted(true);
    setPaused(true);
  }, [manuallyInteracted]);

  return (
    <section
      ref={rootRef}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-24"
      data-testid="portfolio-showcase"
      data-showcase-state={currentState}
      data-showcase-total-ms={TOTAL_MS}
      aria-label={t("landing.showcase_heading")}
    >
      {/* Section introduction */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#3DEBFF]/30 bg-[rgba(61,235,255,0.06)] px-3 py-1 text-[10.5px] uppercase tracking-[0.20em] text-[#3DEBFF] font-medium" data-testid="portfolio-showcase-eyebrow">
          <Sparkles className="w-3 h-3" /> {t("landing.showcase_eyebrow")}
        </div>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground max-w-3xl mx-auto" data-testid="portfolio-showcase-heading">
          {t("landing.showcase_heading")}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto" data-testid="portfolio-showcase-subtitle">
          {t("landing.showcase_subtitle")}
        </p>
      </div>

      {/* Browser frame */}
      <div className="relative rounded-[20px] sm:rounded-[24px] border border-white/10 bg-[rgba(10,12,15,0.82)] shadow-[0_28px_80px_-30px_rgba(0,0,0,0.75)] overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-3 sm:px-4 py-2 border-b border-white/5 bg-black/40">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] opacity-70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e] opacity-70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28ca42] opacity-70" />
          </div>
          <div className="hidden sm:flex flex-1 items-center justify-center min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-0.5 text-[11px] text-muted-foreground" data-testid="showcase-frame-url">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2BFF8A]" />
              stockaily.ai/portfolio
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/30 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="portfolio-showcase-example-badge">
              <Info className="w-3 h-3 text-[#3DEBFF]" />
              {t("landing.showcase_example_badge")}
            </div>
            <button
              type="button"
              onClick={onManualPause}
              aria-label={paused ? t("landing.showcase_play") : t("landing.showcase_pause")}
              data-testid="portfolio-showcase-toggle"
              data-toggle-location="chrome"
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/30 px-2 py-0.5 text-[10.5px] text-muted-foreground hover:text-foreground hover:bg-black/50 transition-colors"
            >
              {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              <span className="hidden md:inline">{paused ? t("landing.showcase_play") : t("landing.showcase_pause")}</span>
            </button>
          </div>
        </div>

        <div className="relative">
          {reducedMotion ? (
            <div className="absolute top-2 left-2 z-20 rounded-full border border-white/15 bg-black/50 backdrop-blur px-2.5 py-1 text-[10.5px] text-muted-foreground" data-testid="showcase-reduced-motion-notice">
              {t("landing.showcase_reduced_static")}
            </div>
          ) : null}

          <div
            ref={scrollRef}
            className="relative overflow-y-auto max-h-[680px]"
            onMouseEnter={onManualInteract}
            onFocus={onManualInteract}
            data-testid="showcase-scroll-area"
          >
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-foreground" data-testid="showcase-header-title">{t("landing.showcase_header_title")}</h3>
                  <p className="text-[12.5px] sm:text-sm text-muted-foreground max-w-[46ch]">{t("landing.showcase_header_subtitle")}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="showcase-header-actions">
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-card/50 px-3 py-1.5 text-[12px] text-foreground/85 hover:bg-white/[0.03]">
                    <RefreshCcw className="w-3.5 h-3.5" />
                    {t("landing.showcase_refresh_prices")}
                  </button>
                  <button
                    type="button"
                    ref={analyzeBtnRef}
                    data-testid="showcase-analyze-btn"
                    data-analyze-state={showLoading ? "loading" : showCompleted ? "completed" : "idle"}
                    disabled={showLoading}
                    className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-all ${
                      showLoading
                        ? "border border-[#3DEBFF]/30 bg-[rgba(61,235,255,0.04)] text-[#3DEBFF]/60 cursor-not-allowed"
                        : "border border-[#3DEBFF]/60 bg-[rgba(61,235,255,0.12)] text-foreground shadow-[0_0_0_1.5px_rgba(61,235,255,0.5),0_0_16px_rgba(61,235,255,0.22)]"
                    } ${analyzeHover && !showLoading ? "ring-2 ring-[#3DEBFF]/40" : ""} ${analyzePressed ? "scale-[0.98]" : ""}`}
                  >
                    {showLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    <span>{buttonLabel}</span>
                    <span className="ml-1 rounded-full border border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.14)] px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.16em] text-[#3DEBFF]">
                      {t("landing.showcase_premium_ai")}
                    </span>
                  </button>
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-card/50 px-3 py-1.5 text-[12px] text-foreground/85 hover:bg-white/[0.03]">
                    <Plus className="w-3.5 h-3.5" />
                    {t("landing.showcase_add_holding")}
                  </button>
                </div>
              </div>

              {/* KPI cards */}
              <KpiCards t={t} />

              {/* Loading banner (only during loading state) */}
              {showLoading ? <LoadingBanner t={t} /> : null}

              {/* Growth chart */}
              <GrowthChart showTooltip={showTooltip} reducedMotion={reducedMotion} t={t} chartRef={chartRef} graphPointRef={graphPointRef} />

              {/* Insight cards */}
              <div ref={insightsRef}>
                <InsightCards highlightNEE={highlightNEE} t={t} />
              </div>

              {/* Holdings table */}
              <HoldingsTable highlightTicker={highlightNEE ? "NEE" : null} t={t} />

              {/* Profit / Loss Breakdown */}
              <div ref={plRef}>
                <ProfitLossBreakdown t={t} />
              </div>

              {/* AI Portfolio Review */}
              <div ref={reviewRef}>
                <AIPortfolioReview t={t} />
              </div>
            </div>

            {/* Simulated cursor overlay */}
            {cursorTarget ? <FakeCursor {...cursorTarget} /> : null}
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-[11.5px] text-muted-foreground/85" data-testid="portfolio-showcase-disclosure">
        {t("landing.showcase_disclosure")}
      </p>
    </section>
  );
}
